"use client";

import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { WIDTH_2COL } from "@constants/global";
import { gqlConversation, gqlCorporateValues, gqlFeedback, gqlMessage } from "gql";
import useTranslation from "next-translate/useTranslation";
import { Plus, Loader2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import type { IVendorUserList, IVendorUserListResponse } from "gql/CorporateValues/queries";
import type { IMessage } from "types/Message.type";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import SearchUser from "components/_Pages/Maintainers/CorporateValue/Recognition/searchUser";
import {
  Button as ButtonV2,
  isRenderableImageSrc,
  LiquidPill,
  Modal,
  PresenceDot,
  UserTooltip,
} from "components/ui/v2";
import { ExploreMobileSidePanelV2 } from "components/ui/v2/explore/ExploreMobileSidePanelV2";
import { StoryViewerModal, type StoryGroup } from "./StoryViewerModal";
import dynamic from "next/dynamic";
import { useChatPresenceHeartbeat } from "hooks/useChatPresenceHeartbeat";
import { useChatStore } from "stores/chat.store";
import { putToSignedUrl } from "utils/storage-upload";

const StoryEditorV2 = dynamic(
  () => import("components/ui/v2/explore/StoryEditorV2"),
  { ssr: false }
);

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function RailAvatar({ src, name }: { src?: string | null; name: string }) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [src]);
  const show = isRenderableImageSrc(src) && !imgError;
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  if (show) {
    return (
      <img
        src={src}
        alt={name}
        className="pointer-events-none h-full w-full select-none rounded-full object-cover"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <span className="pointer-events-none flex h-full w-full select-none items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 text-sm font-semibold text-indigo-700 dark:from-indigo-900/40 dark:to-violet-900/40 dark:text-indigo-300">
      {initials}
    </span>
  );
}

// Story upload constraints
const MAX_STORY_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_STORY_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
]);

type RailUser = {
  id: string;
  name?: string;
  lastname?: string;
  profileImage?: string;
};

type RailItem = {
  userId: string;
  user?: RailUser;
  kind: "story" | "presence";
  storyGroup?: StoryGroup;
  online: boolean;
};

const groupHasUnseen = (group: StoryGroup) =>
  group.stories.some((story) => story.viewedByMe === false);

const storyAvatarRingClass = (unseen: boolean) =>
  unseen ? "explore-story-avatar-recent" : "ring-2 ring-indigo-400/70";

const getStoryVisibleUser = (story: IMessage) => story.ownerUser || story.createdBy;

const groupStoriesByUser = (stories: IMessage[]): StoryGroup[] => {
  const groupMap = new Map<string, StoryGroup>();

  stories.forEach((story) => {
    const visibleUser = getStoryVisibleUser(story);
    const userId = visibleUser?.id || story.ownerUserId || story.createdBy?.id || story.id;
    const currentGroup = groupMap.get(userId);

    if (currentGroup) {
      currentGroup.stories.push(story);
      if (+new Date(story.createdAt) > +new Date(currentGroup.latestStory.createdAt)) {
        currentGroup.latestStory = story;
      }
      return;
    }

    groupMap.set(userId, {
      userId,
      user: visibleUser,
      latestStory: story,
      stories: [story],
    });
  });

  return [...groupMap.values()].map((group) => ({
    ...group,
    stories: [...group.stories].sort(
      (a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)
    ),
  }));
};

const toRailUser = (user?: RailUser | null): RailUser | undefined => {
  if (!user?.id) return undefined;
  return {
    id: user.id,
    name: user.name,
    lastname: user.lastname,
    profileImage: user.profileImage,
  };
};

export function ExploreStoriesInline() {
  const { t } = useTranslation("explore");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedInviteUser, setSelectedInviteUser] = useState<IVendorUserList | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [editorImageUrl, setEditorImageUrl] = useState<string | null>(null);
  const [editorPublishing, setEditorPublishing] = useState(false);
  const [isWideViewport, setIsWideViewport] = useState(false);
  const { user } = useUserStoreContext((store) => ({ user: store.user }));
  const { openChatWithUser, pendingOpenUserId } = useChatStore();

  useChatPresenceHeartbeat();

  // Track the active blob URL so we can always revoke it (avoid memory leaks).
  const activeBlobUrlRef = useRef<string | null>(null);

  const releaseEditorImage = useCallback(() => {
    if (activeBlobUrlRef.current) {
      URL.revokeObjectURL(activeBlobUrlRef.current);
      activeBlobUrlRef.current = null;
    }
    setEditorImageUrl(null);
  }, []);

  // Safety net: revoke on unmount.
  useEffect(
    () => () => {
      if (activeBlobUrlRef.current) {
        URL.revokeObjectURL(activeBlobUrlRef.current);
        activeBlobUrlRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia(`(min-width: ${WIDTH_2COL}px)`);
    const sync = () => setIsWideViewport(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener("change", sync);
    return () => mediaQuery.removeEventListener("change", sync);
  }, []);

  const { data, refetch } = useQuery<{ getStories: IMessage[] }>(
    gqlMessage.queries.GET_STORIES,
    { fetchPolicy: "cache-and-network" }
  );

  const {
    data: vendorUsersData,
    loading: vendorUsersLoading,
    error: vendorUsersError,
  } = useQuery<IVendorUserListResponse>(
    gqlCorporateValues.queries.GET_SIMPLE_VENDOR_USER_LIST,
    { fetchPolicy: "cache-first" }
  );

  const vendorUsers = useMemo(
    () => vendorUsersData?.vendorUsersListWithoutUserLogin ?? [],
    [vendorUsersData?.vendorUsersListWithoutUserLogin]
  );

  const presenceUserIds = useMemo(
    () =>
      vendorUsers
        .map((vendorUser) => vendorUser.id)
        .filter((id): id is string => Boolean(id) && id !== user?.id)
        .slice(0, 200),
    [vendorUsers, user?.id]
  );

  const { data: onlineStatusData } = useQuery<{
    usersOnlineStatus: Array<{
      userId: string;
      online: boolean;
      lastSeenAt?: number | null;
    }>;
  }>(gqlConversation.queries.USERS_ONLINE_STATUS, {
    variables: { userIds: presenceUserIds },
    skip: presenceUserIds.length === 0,
    fetchPolicy: "network-only",
    pollInterval: 30000,
  });

  const onlineByUserId = useMemo(() => {
    const map = new Map<string, boolean>();
    onlineStatusData?.usersOnlineStatus?.forEach((entry) => {
      map.set(entry.userId, entry.online);
    });
    return map;
  }, [onlineStatusData?.usersOnlineStatus]);

  const lastSeenByUserId = useMemo(() => {
    const map = new Map<string, number>();
    onlineStatusData?.usersOnlineStatus?.forEach((entry) => {
      if (entry.lastSeenAt != null) map.set(entry.userId, entry.lastSeenAt);
    });
    return map;
  }, [onlineStatusData?.usersOnlineStatus]);

  const isRecentlyOffline = useCallback(
    (userId: string) => {
      if (onlineByUserId.get(userId) === true) return false;
      const lastSeen = lastSeenByUserId.get(userId);
      if (lastSeen == null) return false;
      return Date.now() - lastSeen <= SEVEN_DAYS_MS;
    },
    [onlineByUserId, lastSeenByUserId]
  );

  const teamMemberIds = useMemo(() => {
    const ids = new Set<string>();
    user?.teams?.forEach((team) => {
      team.user?.forEach((member) => {
        if (member?.id) ids.add(member.id);
      });
      team.leader?.forEach((leader) => {
        if (leader?.id) ids.add(leader.id);
      });
    });
    if (user?.id) ids.delete(user.id);
    return ids;
  }, [user?.id, user?.teams]);

  const [signedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, {
    fetchPolicy: "network-only",
  });

  const [createStory] = useMutation(gqlMessage.mutations.CREATE_STORY);
  const [sendStoryInvitation, { loading: sendingInvitation }] = useMutation(
    gqlMessage.mutations.SEND_STORY_INVITATION
  );
  const [storyReaction] = useMutation(gqlMessage.mutations.STORY_REACTION);
  const [deleteStory] = useMutation(gqlMessage.mutations.DELETE_STORY);

  const stories = useMemo(
    () =>
      [...(data?.getStories || [])].sort(
        (a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)
      ),
    [data?.getStories]
  );

  const storyGroups = useMemo(() => groupStoriesByUser(stories), [stories]);

  const myStoryGroup = useMemo(
    () => storyGroups.find((group) => group.userId === user?.id) || null,
    [storyGroups, user?.id]
  );

  const maxSlots = isWideViewport ? 12 : 7;
  const remainingSlots = Math.max(0, maxSlots - 1);
  const presenceOnlyCap = Math.floor(maxSlots * 0.35);

  const railItems = useMemo(() => {
    const used = new Set<string>();
    const items: RailItem[] = [];

    const otherStoryGroups = storyGroups
      .filter((group) => group.userId !== user?.id)
      .sort((a, b) => {
        const aUnseen = groupHasUnseen(a) ? 1 : 0;
        const bUnseen = groupHasUnseen(b) ? 1 : 0;
        if (aUnseen !== bUnseen) return bUnseen - aUnseen;

        const aTeam = teamMemberIds.has(a.userId) ? 1 : 0;
        const bTeam = teamMemberIds.has(b.userId) ? 1 : 0;
        if (aTeam !== bTeam) return bTeam - aTeam;

        return (
          +new Date(b.latestStory.createdAt) - +new Date(a.latestStory.createdAt)
        );
      });

    for (const group of otherStoryGroups) {
      if (items.length >= remainingSlots) break;
      if (used.has(group.userId)) continue;
      used.add(group.userId);
      items.push({
        userId: group.userId,
        user: toRailUser(group.user),
        kind: "story",
        storyGroup: group,
        online: onlineByUserId.get(group.userId) === true,
      });
    }

    const presenceCandidates = vendorUsers
      .filter(
        (vendorUser) =>
          vendorUser.id &&
          vendorUser.id !== user?.id &&
          !used.has(vendorUser.id) &&
          onlineByUserId.get(vendorUser.id) === true
      )
      .sort((a, b) => {
        const aTeam = teamMemberIds.has(a.id) ? 1 : 0;
        const bTeam = teamMemberIds.has(b.id) ? 1 : 0;
        return bTeam - aTeam;
      });

    let presenceOnlyCount = 0;
    for (const vendorUser of presenceCandidates) {
      if (items.length >= remainingSlots) break;
      if (presenceOnlyCount >= presenceOnlyCap) break;
      used.add(vendorUser.id);
      presenceOnlyCount += 1;
      items.push({
        userId: vendorUser.id,
        user: toRailUser(vendorUser),
        kind: "presence",
        online: true,
      });
    }

    // After online: offline fillers by lastSeenAt (most recent first).
    const fillerCandidates = vendorUsers
      .filter(
        (vendorUser) =>
          vendorUser.id &&
          vendorUser.id !== user?.id &&
          !used.has(vendorUser.id) &&
          onlineByUserId.get(vendorUser.id) !== true
      )
      .sort((a, b) => {
        const lastSeenDiff =
          (lastSeenByUserId.get(b.id) ?? 0) - (lastSeenByUserId.get(a.id) ?? 0);
        if (lastSeenDiff !== 0) return lastSeenDiff;
        const aTeam = teamMemberIds.has(a.id) ? 1 : 0;
        const bTeam = teamMemberIds.has(b.id) ? 1 : 0;
        return bTeam - aTeam;
      });

    for (const vendorUser of fillerCandidates) {
      if (items.length >= remainingSlots) break;
      used.add(vendorUser.id);
      items.push({
        userId: vendorUser.id,
        user: toRailUser(vendorUser),
        kind: "presence",
        online: false,
      });
    }

    return items;
  }, [
    storyGroups,
    user?.id,
    remainingSlots,
    presenceOnlyCap,
    teamMemberIds,
    vendorUsers,
    onlineByUserId,
    lastSeenByUserId,
  ]);

  // Viewer set: own story (if any) + rail story items in relative rail order.
  const viewerStoryGroups = useMemo(() => {
    const fromRail = railItems
      .filter((item) => item.kind === "story" && item.storyGroup)
      .map((item) => item.storyGroup as StoryGroup);
    return [myStoryGroup, ...fromRail].filter(Boolean) as StoryGroup[];
  }, [myStoryGroup, railItems]);

  const viewerIndexByUserId = useMemo(() => {
    const map = new Map<string, number>();
    viewerStoryGroups.forEach((group, index) => {
      map.set(group.userId, index);
    });
    return map;
  }, [viewerStoryGroups]);

  const shouldShowStoryInvitationButton = 1 + railItems.length < maxSlots;

  const openViewer = (index: number) => {
    setViewerIndex(index);
    setIsViewerOpen(true);
  };

  const handleOpenPresenceChat = (userId: string) => {
    openChatWithUser(userId);
  };

  const handleSelectStory = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Always reset the input so re-selecting the same file fires onChange.
    event.target.value = "";
    if (!file) return;

    const mime = (file.type || "").toLowerCase();
    if (!mime.startsWith("image/") || !ALLOWED_STORY_MIME_TYPES.has(mime)) {
      toast.error(t("Formato no soportado. Usa JPG, PNG, WEBP o GIF"));
      return;
    }

    if (file.size > MAX_STORY_FILE_BYTES) {
      const mb = (MAX_STORY_FILE_BYTES / (1024 * 1024)).toFixed(0);
      toast.error(`${t("La imagen supera el tamaño máximo")} (${mb} MB)`);
      return;
    }

    // Ensure we don't leak a previous blob URL if the user re-selects.
    if (activeBlobUrlRef.current) {
      URL.revokeObjectURL(activeBlobUrlRef.current);
      activeBlobUrlRef.current = null;
    }

    const localUrl = URL.createObjectURL(file);
    activeBlobUrlRef.current = localUrl;
    setEditorImageUrl(localUrl);
  };

  const handleEditorPublish = async (dataUrl: string) => {
    try {
      setEditorPublishing(true);

      // Convert dataURL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Upload — use uuid for true uniqueness (the back also prefixes with uuid,
      // but keeping the client key unique avoids ambiguity in logs/debugging).
      const key = `story-${uuid()}.jpg`;
      const result = await signedUrl({ variables: { key } });
      const putUrl = result.data?.getSignedUrl as string | undefined;
      if (!putUrl) throw new Error("SIGNED_URL_NOT_FOUND");

      const imageUrl = await putToSignedUrl(putUrl, blob, "image/jpeg").catch(
        () => {
          throw new Error("UPLOAD_FAILED");
        }
      );

      // NOTE: if createStory fails after the upload succeeded the file remains
      // orphan in the bucket. A proper fix needs a backend cleanup hook; we
      // surface the error so the user can retry.
      await createStory({
        variables: {
          createStoryInput: {
            thumbnail: imageUrl,
            text: "",
          },
        },
        // Optimistic cache update: insert the new story at the head of
        // getStories so the strip updates immediately, no flicker.
        update: (cache, { data: mutationData }) => {
          const created = mutationData?.createStory as IMessage | undefined;
          if (!created) return;
          try {
            const existing = cache.readQuery<{ getStories: IMessage[] }>({
              query: gqlMessage.queries.GET_STORIES,
            });
            cache.writeQuery({
              query: gqlMessage.queries.GET_STORIES,
              data: {
                getStories: [created, ...(existing?.getStories || [])],
              },
            });
          } catch {
            // Cache miss — fall back to refetch below.
          }
        },
      });

      toast.success(t("Story publicada"));
      releaseEditorImage();
    } catch {
      toast.error(t("No se pudo subir la story"));
      // Re-sync from the server in case the mutation actually succeeded but
      // the optimistic update path errored.
      refetch().catch(() => undefined);
    } finally {
      setEditorPublishing(false);
    }
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setSelectedInviteUser(null);
  };

  const handleSendStoryInvitation = async () => {
    if (!selectedInviteUser?.email) return;

    try {
      await sendStoryInvitation({ variables: { email: selectedInviteUser.email } });
      toast.success(t("Invitación enviada"));
      handleCloseInviteModal();
    } catch {
      toast.error(t("No se pudo enviar la invitación"));
    }
  };

  const handleStoryReaction = async (storyId: string, emoji: string) => {
    try {
      await storyReaction({
        variables: { storyReactionInput: { story: storyId, emoji } },
      });
      await refetch();
    } catch {
      toast.error(t("No se pudo reaccionar"));
    }
  };

  const handleDeleteStory = async (storyId: string) => {
    try {
      await deleteStory({ variables: { id: storyId } });
      await refetch();
      setIsViewerOpen(false);
      toast.success(t("Story eliminada"));
    } catch {
      toast.error(t("No se pudo eliminar la story"));
    }
  };

  return (
    <>
      <style jsx global>{`
        @keyframes explore-story-ring-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .explore-story-avatar-recent {
          position: relative;
          display: block;
          width: 100%;
          height: 100%;
          overflow: visible;
          border-radius: 9999px;
        }

        .explore-story-avatar-recent::before {
          content: "";
          position: absolute;
          inset: -2px;
          border-radius: 9999px;
          pointer-events: none;
          background: conic-gradient(
            from 0deg,
            #6366f1 0%,
            #8b5cf6 8.33%,
            #a855f7 16.66%,
            #d946ef 25%,
            #ec4899 33.33%,
            #f43f5e 41.66%,
            #f59e0b 50%,
            #eab308 58.33%,
            #84cc16 66.66%,
            #22c55e 75%,
            #14b8a6 83.33%,
            #22d3ee 91.66%,
            #6366f1 100%
          );
          animation: explore-story-ring-spin 2.2s linear infinite;
          filter: saturate(1.6) brightness(1.15);
          z-index: -1;
        }

        .explore-story-avatar-recent > img,
        .explore-story-avatar-recent > span {
          position: relative;
          z-index: 0;
        }

        .explore-story-avatar-recent > img {
          border: 2px solid white;
          border-radius: 9999px;
        }

        .dark .explore-story-avatar-recent > img {
          border-color: rgb(24 24 27);
        }
      `}</style>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleSelectStory}
      />

      <div className="mb-0.5 px-2 pt-2">
        <div className="flex items-center gap-2">
          <div className="-my-2 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-1 py-2">
          {myStoryGroup ? (
            <div className="group relative h-12 w-12 shrink-0 transition hover:z-20 hover:scale-105">
              {user?.id ? (
                <UserTooltip
                  user={{
                    id: user.id,
                    name: user.name,
                    lastname: user.lastname,
                    profileImage: user.profileImage,
                  }}
                  side="bottom"
                  triggerClassName="h-12 w-12 shrink-0 cursor-pointer"
                >
                  <button
                    type="button"
                    onClick={() => openViewer(0)}
                    className="relative z-0 block h-full w-full cursor-pointer overflow-visible rounded-full"
                    aria-label={t("Ver tu story")}
                  >
                    <span
                      className={`block h-full w-full rounded-full ${storyAvatarRingClass(
                        groupHasUnseen(myStoryGroup)
                      )}`}
                    >
                      <RailAvatar
                        src={user?.profileImage}
                        name={`${user?.name || ""} ${user?.lastname || ""}`.trim() || t("Tu story")}
                      />
                    </span>
                  </button>
                </UserTooltip>
              ) : (
                <button
                  type="button"
                  onClick={() => openViewer(0)}
                  className="relative z-0 block h-full w-full cursor-pointer overflow-visible rounded-full"
                  aria-label={t("Ver tu story")}
                >
                  <span
                    className={`block h-full w-full rounded-full ${storyAvatarRingClass(
                      groupHasUnseen(myStoryGroup)
                    )}`}
                  >
                    <RailAvatar
                      src={user?.profileImage}
                      name={`${user?.name || ""} ${user?.lastname || ""}`.trim() || t("Tu story")}
                    />
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
                className="absolute -bottom-0.5 -right-0.5 z-10 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-indigo-600 text-white shadow-[0_2px_8px_rgba(79,70,229,0.28)] ring-2 ring-white transition hover:scale-110 disabled:opacity-60 dark:ring-zinc-900"
                aria-label={t("Subir story")}
                title={t("Subir story")}
              >
                {uploading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Plus className="h-2.5 w-2.5" strokeWidth={3} />}
              </button>
            </div>
          ) : (
            <div className="relative h-12 w-12 shrink-0 transition hover:z-20 hover:scale-105">
              {user?.id ? (
                <UserTooltip
                  user={{
                    id: user.id,
                    name: user.name,
                    lastname: user.lastname,
                    profileImage: user.profileImage,
                  }}
                  side="bottom"
                  triggerClassName="h-12 w-12 shrink-0 cursor-pointer"
                >
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="relative z-0 h-full w-full cursor-pointer overflow-visible rounded-full disabled:opacity-60"
                    aria-label={t("Subir story")}
                  >
                    <span className="block h-full w-full overflow-hidden rounded-full ring-2 ring-indigo-400/70">
                      <RailAvatar
                        src={user?.profileImage}
                        name={`${user?.name || ""} ${user?.lastname || ""}`.trim() || t("Tu story")}
                      />
                    </span>
                    <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white shadow-[0_2px_8px_rgba(79,70,229,0.28)] ring-2 ring-white dark:ring-zinc-900">
                      {uploading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Plus className="h-2.5 w-2.5" strokeWidth={3} />}
                    </span>
                  </button>
                </UserTooltip>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="relative z-0 h-full w-full cursor-pointer overflow-visible rounded-full disabled:opacity-60"
                  aria-label={t("Subir story")}
                >
                  <span className="block h-full w-full overflow-hidden rounded-full ring-2 ring-indigo-400/70">
                    <RailAvatar
                      src={user?.profileImage}
                      name={`${user?.name || ""} ${user?.lastname || ""}`.trim() || t("Tu story")}
                    />
                  </span>
                  <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white shadow-[0_2px_8px_rgba(79,70,229,0.28)] ring-2 ring-white dark:ring-zinc-900">
                    {uploading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Plus className="h-2.5 w-2.5" strokeWidth={3} />}
                  </span>
                </button>
              )}
            </div>
          )}

          {railItems.map((item, index) => {
            const displayName =
              `${item.user?.name || ""} ${item.user?.lastname || ""}`.trim();
            const isCreatingThis = pendingOpenUserId === item.userId;
            const showPresenceSeparator =
              item.kind === "presence" &&
              railItems.slice(0, index).every((r) => r.kind === "story");
            const tooltipUser = item.userId
              ? {
                  id: item.userId,
                  name: item.user?.name,
                  lastname: item.user?.lastname,
                  profileImage: item.user?.profileImage,
                }
              : null;

            if (item.kind === "story" && item.storyGroup) {
              const viewerIdx = viewerIndexByUserId.get(item.userId) ?? 0;
              const storyButton = (
                <button
                  type="button"
                  onClick={() => openViewer(viewerIdx)}
                  className="relative z-0 h-full w-full cursor-pointer overflow-visible rounded-full"
                  aria-label={`${t("Ver story de")} ${item.user?.name || ""}`}
                >
                  <span
                    className={`block h-full w-full rounded-full ${storyAvatarRingClass(
                      groupHasUnseen(item.storyGroup)
                    )}`}
                  >
                    <RailAvatar src={item.user?.profileImage} name={displayName} />
                  </span>
                  <span className="pointer-events-none absolute bottom-0 right-0">
                    {item.online ? (
                      <PresenceDot status="online" size="sm" ringed />
                    ) : isRecentlyOffline(item.userId) ? (
                      <PresenceDot status="offline" size="sm" ringed />
                    ) : null}
                  </span>
                </button>
              );

              return (
                <div
                  key={`story-${item.userId}`}
                  className="relative h-12 w-12 shrink-0 transition hover:z-20 hover:scale-105"
                >
                  {tooltipUser ? (
                    <UserTooltip
                      user={tooltipUser}
                      side="bottom"
                      triggerClassName="h-12 w-12 shrink-0 cursor-pointer"
                    >
                      {storyButton}
                    </UserTooltip>
                  ) : (
                    storyButton
                  )}
                </div>
              );
            }

            const presenceButton = (
              <button
                type="button"
                data-open-chat-with-user
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handleOpenPresenceChat(item.userId);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPresenceChat(item.userId);
                }}
                disabled={!!pendingOpenUserId && !isCreatingThis}
                className="relative z-0 h-full w-full cursor-pointer overflow-visible rounded-full disabled:opacity-60"
                aria-label={displayName || t("Iniciar conversación")}
              >
                <span className="block h-full w-full overflow-hidden rounded-full">
                  <RailAvatar src={item.user?.profileImage} name={displayName} />
                </span>
                <span className="pointer-events-none absolute bottom-0 right-0">
                  {isCreatingThis ? (
                    <Loader2 className="h-2.5 w-2.5 animate-spin text-emerald-500" />
                  ) : item.online ? (
                    <PresenceDot status="online" size="sm" ringed />
                  ) : isRecentlyOffline(item.userId) ? (
                    <PresenceDot status="offline" size="sm" ringed />
                  ) : null}
                </span>
              </button>
            );

            const presenceNode = tooltipUser ? (
              <UserTooltip
                user={tooltipUser}
                side="bottom"
                triggerClassName="h-12 w-12 shrink-0 cursor-pointer"
              >
                {presenceButton}
              </UserTooltip>
            ) : (
              presenceButton
            );

            if (!showPresenceSeparator) {
              return (
                <div
                  key={`presence-${item.userId}`}
                  className="relative h-12 w-12 shrink-0 transition hover:z-20 hover:scale-105"
                >
                  {presenceNode}
                </div>
              );
            }

            return (
              <Fragment key={`presence-${item.userId}`}>
                <span
                  aria-hidden
                  className="mx-1 h-7 w-px shrink-0 self-center rounded-full bg-slate-300/40 dark:bg-zinc-600/30"
                />
                <div className="relative h-12 w-12 shrink-0 transition hover:z-20 hover:scale-105">
                  {presenceNode}
                </div>
              </Fragment>
            );
          })}

          {shouldShowStoryInvitationButton && (
            <span title={t("Esto enviará un correo a un colega para que cree una story en Groowcity")}>
              <LiquidPill
                as="button"
                type="button"
                variant="slate"
                appearance="normal"
                shape="circle"
                size="lg"
                icon={
                  sendingInvitation ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <UserPlus className="h-6 w-6" strokeWidth={1.8} />
                  )
                }
                onClick={() => setIsInviteModalOpen(true)}
                disabled={sendingInvitation}
                className="!h-12 !w-12 hover:z-10"
                aria-label={t("Invita a un colega a publicar algo")}
              />
            </span>
          )}
          </div>

          {/*
            Toggle del side-panel: solo se muestra cuando el panel de widgets
            está oculto (mismo breakpoint que ExplorePageLayoutV2:
            `WIDTH_2COL = 1140px`). En desktop el panel ya está visible a la
            derecha y este botón sobra. En mobile/tablet aparece a la derecha
            de las historias.
          */}
          <div className="shrink-0 min-[1140px]:hidden">
            <ExploreMobileSidePanelV2 render={true} />
          </div>
        </div>
      </div>

      <Modal
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        size="sm"
        panelClassName="overflow-visible"
        bodyClassName="overflow-visible"
        footerClassName="rounded-b-[24px]"
        title={t("Invita a un colega a publicar algo")}
        description={t("Busca a un colega y le enviaremos un correo para que cree una story en Groowcity")}
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <ButtonV2
              type="button"
              variant="secondary"
              onClick={handleCloseInviteModal}
              disabled={sendingInvitation}
            >
              {t("Cancelar")}
            </ButtonV2>
            <ButtonV2
              type="button"
              onClick={handleSendStoryInvitation}
              loading={sendingInvitation}
              disabled={!selectedInviteUser || sendingInvitation}
            >
              {t("Enviar invitación")}
            </ButtonV2>
          </div>
        }
      >
        <div className="space-y-4 px-4 pt-2">
          <SearchUser
            selectedUser={selectedInviteUser}
            setSelectedUser={setSelectedInviteUser}
            loading={vendorUsersLoading}
            error={vendorUsersError}
            data={vendorUsersData}
            inputVariant="violet"
          />

          {selectedInviteUser && (
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200">
              {t("Se enviará la invitación a")} {selectedInviteUser.email}
            </div>
          )}
        </div>
      </Modal>

      {isViewerOpen && viewerStoryGroups.length > 0 && (
        <StoryViewerModal
          storyGroups={viewerStoryGroups}
          initialGroupIndex={viewerIndex}
          currentUserId={user?.id}
          onClose={() => setIsViewerOpen(false)}
          onReact={handleStoryReaction}
          onDelete={handleDeleteStory}
        />
      )}

      {editorImageUrl && (
        <StoryEditorV2
          imageUrl={editorImageUrl}
          publishing={editorPublishing}
          onPublish={handleEditorPublish}
          onClose={releaseEditorImage}
        />
      )}
    </>
  );
}
